import { ChevronLeft } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Alert } from '@/components/ui/alert'
import { ApiError, getJSON } from '@/lib/api'
import type { Association } from '@/pages/StaffSignInPage'
import { PublicNotFoundPage, PublicPageShell } from './PublicPageShell'

type DirectoryField = {
  key: string
  label: string
  value: string | number | string[] | { thumbnail_url: string; original_url?: string }
}
type ProfileResponse = { member_directory: { member?: { name: string; fields: DirectoryField[] } } }

export function PublicMemberProfilePage({
  association,
  slug,
}: {
  association: Association
  slug: string
}) {
  const query = useQuery<ProfileResponse>({
    queryKey: ['public-member-profile', slug],
    queryFn: () => getJSON(`/api/v1/public_member_directory/${encodeURIComponent(slug)}`),
    retry: false,
  })
  if (query.isLoading)
    return (
      <PublicPageShell association={association}>
        <p className="py-16 text-body-sm text-muted-foreground" role="status">
          Loading member profile…
        </p>
      </PublicPageShell>
    )
  if (query.error instanceof ApiError && query.error.status === 404)
    return <PublicNotFoundPage association={association} />
  if (query.isError)
    return (
      <PublicPageShell association={association}>
        <Alert>Member profile unavailable.</Alert>
      </PublicPageShell>
    )
  if (!query.data?.member_directory.member) return <PublicNotFoundPage association={association} />
  const member = query.data.member_directory.member
  return <PublicMemberProfileView association={association} member={member} />
}

function PublicMemberProfileView({
  association,
  member,
}: {
  association: Association
  member: NonNullable<ProfileResponse['member_directory']['member']>
}) {
  const field = (key: string) => member.fields.find((item) => item.key === key)
  const photo = field('profile_photo')?.value
  const photoUrl =
    typeof photo === 'object' && photo !== null && !Array.isArray(photo)
      ? photo.original_url
      : undefined
  const level = field('membership_level')?.value
  const organizations = field('organization_affiliations')?.value
  const organizationText = Array.isArray(organizations) ? organizations.join(', ') : organizations
  const factRows = member.fields.filter(
    ({ key, value }) =>
      ![
        'first_name',
        'last_name',
        'membership_level',
        'organization_affiliations',
        'profile_photo',
        'email',
        'phone',
      ].includes(key) && value !== '',
  )
  return (
    <PublicPageShell association={association}>
      <div className="max-w-2xl space-y-6">
        <Link
          to="/directory"
          className="inline-flex items-center gap-1.5 text-body-sm text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="size-4" aria-hidden="true" />
          Directory
        </Link>
        <div className="flex items-center gap-4">
          {photoUrl ? (
            <img src={photoUrl} alt="" className="size-24 shrink-0 rounded-md object-cover" />
          ) : (
            <div className="flex size-24 shrink-0 items-center justify-center rounded-md bg-muted">
              <span className="text-heading-sm font-medium text-muted-foreground">
                {initials(member.name)}
              </span>
            </div>
          )}
          <div className="min-w-0 space-y-0.5 leading-snug">
            {level !== undefined && (
              <p className="text-caption font-medium text-primary">{String(level)}</p>
            )}
            <h1 className="text-subheading font-semibold">{member.name}</h1>
            {organizationText && (
              <p className="text-body-sm text-muted-foreground">{String(organizationText)}</p>
            )}
          </div>
        </div>
        {factRows.length > 0 && (
          <div className="overflow-hidden rounded-md border border-border bg-card">
            {factRows.map((row) => (
              <div
                key={row.key}
                className="flex gap-4 border-b border-border px-4 py-2 last:border-b-0"
              >
                <span className="w-40 shrink-0 text-caption text-muted-foreground">
                  {row.label}
                </span>
                <span className="min-w-0 flex-1 text-body-sm">
                  {Array.isArray(row.value) ? row.value.join(', ') : String(row.value)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </PublicPageShell>
  )
}

function initials(name: string) {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
}
